import moment from 'moment'
import {
  TIME_FORMAT_12_HRS,
  TIME_FORMAT_24_HRS,
  DATE_TIME_FORMAT_24_HRS,
  NUMBER_OF_MILLISECONDS_IN_DAY,
  DISPLAY_DATE_FORMAT
} from '@/utils/constants'

export const getLocalDateTime = (dateTime: Date | string, format?: string) =>
  moment
    .utc(dateTime)
    .local()
    .format(format ?? DATE_TIME_FORMAT_24_HRS)
export const getLocalDate = (dateTime: Date | string | null, format?: string) =>
  moment
    .utc(dateTime)
    .local()
    .format(format ?? DISPLAY_DATE_FORMAT)
export const getLocalTime = (dateTime: string) =>
  moment.utc(dateTime).local().format(TIME_FORMAT_12_HRS)
export const unixTimestampToUTC = (unixTimestamp: number) =>
  moment(unixTimestamp).utc().toISOString()
export const unixTimestampToDateOnly = (unixTimestamp: number) =>
  moment(unixTimestamp).utc().local().format('YYYY-MM-DD')
export const toUTCDate = (dateTime: string | Date) => moment(dateTime).utc().format()
export const getLocalTimeDisplayFormat = (time: string) =>
  moment(time, 'HH:mm:ss').format('hh:mm A')

export const convertUnixTimestampToUTC = (unixTimestamp: number) => {
  const date = new Date(unixTimestamp)

  const year = date.getUTCFullYear()
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const day = date.getUTCDate().toString().padStart(2, '0')
  const hours = date.getUTCHours().toString().padStart(2, '0')
  const minutes = date.getUTCMinutes().toString().padStart(2, '0')
  const seconds = date.getUTCSeconds().toString().padStart(2, '0')
  const utcString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`

  return utcString
}

export const convertMinutesToHMS = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  const hoursStr = hours > 0 ? `${hours} h ` : '0 h '
  const minutesStr = remainingMinutes > 0 ? `${Math.floor(remainingMinutes)} m ` : '0 m '

  return `${hoursStr}${minutesStr}`.trim()
}

export const getHoursWithoutMinutes = (minutes: number | undefined) => {
  if (!minutes) return 0
  return Math.floor(minutes / 60)
}

export const getMinutesWithoutHours = (minutes: number | undefined) => {
  if (!minutes) return 0
  return minutes % 60
}

export const getPastDayDateTime = () => {
  const currentDate = Date.now()
  const startDate = currentDate - NUMBER_OF_MILLISECONDS_IN_DAY
  const endDate = currentDate

  const dateTimeRange = {
    start: new Date(startDate).toISOString(),
    end: new Date(endDate).toISOString()
  }

  return dateTimeRange
}

export const formatLastSeenDateTime = (dateTime: string) => {
  const humanizedDateTimeFormat = DATE_TIME_FORMAT_24_HRS

  function pluralize(value: any) {
    return value !== 1 ? 's' : ''
  }

  if (dateTime) {
    const timestamp = moment(dateTime)
    const timeInSeconds = moment().diff(timestamp, 'seconds')
    const timeInMinutes = moment().diff(timestamp, 'minutes')
    const currentDate = moment()

    if (timeInSeconds >= 1 && timeInSeconds <= 59)
      return `${timeInSeconds} second${pluralize(timeInSeconds)} ago`

    if (timeInMinutes >= 1 && timeInMinutes <= 59)
      return `${timeInMinutes} minute${pluralize(timeInMinutes)} ago`

    if (timestamp.isSame(currentDate, 'day'))
      return `Today at ${timestamp.format(TIME_FORMAT_24_HRS)}`

    if (timestamp.isSame(currentDate.clone().subtract(1, 'day'), 'day'))
      return `Yesterday at ${timestamp.format(TIME_FORMAT_24_HRS)}`

    return `${timestamp.format(humanizedDateTimeFormat)}`
  }
  return ''
}

export const getStartOfCurrentDate = () => {
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)
  const utcTimestamp = currentDate.toISOString()

  return utcTimestamp
}

export const getStartOfCurrentDateUnix = () => {
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)
  const unixTimestamp = currentDate.getTime()

  return unixTimestamp
}

export const getEndOfCurrentDateUnix = () => {
  const currentDate = new Date()
  currentDate.setHours(23, 59, 59, 999)
  const unixTimestamp = currentDate.getTime()

  return unixTimestamp
}

export const isDateInFuture = (date: string) => {
  const currentTimestamp = getStartOfCurrentDateUnix()
  const comparedDateTimestamp = new Date(date).setHours(0, 0, 0, 0)
  return currentTimestamp < comparedDateTimestamp
}

export const getStartOfDay = (value: string | number) => {
  const date = moment(value).startOf('day')
  return date
}

export const getEndOfDay = (value: string | number) => {
  const date = moment(value).endOf('day')
  return date
}

export const getNumberOfWeeksBetweenDates = (start: string, end: string) => {
  const startDate = moment(start)
  const endDate = moment(end)
  const startDateStartOfWeek = startDate.startOf('isoWeek').valueOf()
  const numberOfWeeks =
    (endDate.valueOf() - startDateStartOfWeek) / (NUMBER_OF_MILLISECONDS_IN_DAY * 7)
  return Math.ceil(numberOfWeeks)
}
